import { useEffect, useRef, useState } from "react";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../utils/coachExerciseNavigation";
import { durationMinutesToSeconds } from "../utils/trainingDuration";
import { resolveLiveSequence } from "../utils/liveTrainingSequenceResolve";

/**
 * Smooth local sequence/exercise countdown anchored to socket timer values.
 */
export function useLiveTrainingSequenceTimer({
  exercise = null,
  exerciseStateId = null,
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  currentSequenceIndex = null,
  tickMs = 1000,
} = {}) {
  const isStarted = exerciseStateId === TE_STATE_STARTED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;

  const seqTimerRaw = exercise?.sequenceTimer;
  const exTimerRaw = exercise?.timer;
  const seqHasTime = Number(seqTimerRaw) > 0;

  const isWaiting =
    !isStarted && !isPaused
      ? true
      : awaitingManualSequenceStart ||
        awaitingAutoSequenceStart ||
        (isPaused && !seqHasTime);

  const [now, setNow] = useState(() => Date.now());
  const anchorRef = useRef({ seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() });

  useEffect(() => {
    anchorRef.current = { seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() };
    setNow(Date.now());
  }, [seqTimerRaw, exTimerRaw, exercise?.te_id, isPaused]);

  useEffect(() => {
    if (isWaiting || isPaused) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), tickMs);
    return () => window.clearInterval(id);
  }, [isWaiting, isPaused, tickMs]);

  const elapsed = isPaused ? 0 : Math.floor((now - anchorRef.current.at) / 1000);
  const seqDisplay =
    seqTimerRaw != null ? Math.max(0, anchorRef.current.seq - elapsed) : null;
  const exDisplay =
    exTimerRaw != null ? Math.max(0, anchorRef.current.ex - elapsed) : null;

  const { number: seqNumber, obj: currentSeq } = resolveLiveSequence(
    exercise,
    currentSequenceIndex
  );
  const sequenceDurationSec = currentSeq
    ? durationMinutesToSeconds(currentSeq.sequence_duration)
    : 0;

  return {
    isStarted,
    isPaused,
    isWaiting,
    seqDisplay,
    exDisplay,
    seqNumber,
    currentSeq,
    sequenceDurationSec,
  };
}
